import io from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000; // 3 seconds
    this.eventHandlers = new Map();
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT authentication token
   * @param {string} serverUrl - Backend server URL (optional)
   */
  connect(token, serverUrl = null) {
    // Get backend URL from environment or use provided URL
    const backendUrl = serverUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    console.log('🔌 Connecting to WebSocket server:', backendUrl);

    // Initialize Socket.IO client
    this.socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      timeout: 10000,
      autoConnect: true
    });

    // Connection established
    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected:', this.socket.id);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Authenticate
      this.socket.emit('authenticate', token);
    });

    // Authentication response
    this.socket.on('authenticated', (data) => {
      console.log('✅ WebSocket authenticated:', data.email);
      this.triggerHandler('connected', data);
    });

    this.socket.on('authentication-failed', (data) => {
      console.error('❌ WebSocket authentication failed:', data.error);
      this.triggerHandler('authentication-failed', data);
      this.disconnect();
    });

    // Connection lost
    this.socket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket disconnected:', reason);
      this.isConnected = false;
      this.triggerHandler('disconnected', { reason });
    });

    // Reconnection attempts
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      this.reconnectAttempts = attemptNumber;
      console.log(`🔄 WebSocket reconnection attempt ${attemptNumber}/${this.maxReconnectAttempts}`);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`✅ WebSocket reconnected after ${attemptNumber} attempts`);
      this.reconnectAttempts = 0;
      // Re-authenticate
      this.socket.emit('authenticate', token);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ WebSocket reconnection failed');
      this.triggerHandler('reconnect-failed');
    });

    // Connection error
    this.socket.on('connect_error', (error) => {
      console.error('❌ WebSocket connection error:', error.message);
      this.triggerHandler('connection-error', { error: error.message });
    });

    // Pong response (health check)
    this.socket.on('pong', () => {
      // Connection is alive
    });

    // Order closed event (TP/SL hit)
    this.socket.on('order-closed', (data) => {
      console.log('📊 Order closed event received:', data);
      this.triggerHandler('order-closed', data);
    });

    // Start ping interval for connection health
    this.startPingInterval();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting WebSocket');
      this.stopPingInterval();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Register event handler
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  on(event, callback) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(callback);
  }

  /**
   * Remove event handler
   * @param {string} event - Event name
   * @param {function} callback - Callback function to remove
   */
  off(event, callback) {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      const index = handlers.indexOf(callback);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Trigger registered event handlers
   * @param {string} event - Event name
   * @param {object} data - Event data
   */
  triggerHandler(event, data = {}) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in ${event} handler:`, error);
        }
      });
    }
  }

  /**
   * Send ping to server (health check)
   */
  ping() {
    if (this.socket && this.isConnected) {
      this.socket.emit('ping');
    }
  }

  /**
   * Start ping interval
   */
  startPingInterval() {
    this.stopPingInterval(); // Clear existing interval
    this.pingInterval = setInterval(() => {
      this.ping();
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop ping interval
   */
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  isSocketConnected() {
    return this.isConnected && this.socket !== null;
  }

  /**
   * Get connection status
   * @returns {object}
   */
  getStatus() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id || null,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

// Singleton instance
const websocketService = new WebSocketService();

export default websocketService;
